import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';
import { RichTextBlock } from 'prismic-reactjs';

import { RichText } from 'components/Prismic';

interface ISecondaryContent {
  title: RichTextBlock[];
  description: RichTextBlock[];
  leftIcons: Pick<GatsbyImageProps, 'image' | 'alt'>[];
  rightIcons: Pick<GatsbyImageProps, 'image' | 'alt'>[];
}

enum SECONDARY_CONTENT_AREAS {
  leftIcon = 'leftIcon',
  title = 'title',
  description = 'description',
  rightIcon = 'rightIcon',
}

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    grid-template-areas: ${`
      '${SECONDARY_CONTENT_AREAS.leftIcon} ${SECONDARY_CONTENT_AREAS.title}         ${SECONDARY_CONTENT_AREAS.rightIcon}'
      '${SECONDARY_CONTENT_AREAS.leftIcon} ${SECONDARY_CONTENT_AREAS.description}   ${SECONDARY_CONTENT_AREAS.rightIcon}'
      '${SECONDARY_CONTENT_AREAS.leftIcon} .                                              ${SECONDARY_CONTENT_AREAS.rightIcon}'`};
    row-gap: 13.5rem;
    justify-items: center;
    z-index: 1;
    background-color: ${colors.white}26;
    padding-top: 6.5rem;
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    grid-area: ${SECONDARY_CONTENT_AREAS.title};
    justify-content: center;

    & > h2 {
      text-align: center;
      line-height: 0.78;
      letter-spacing: -0.1em;
      color: ${colors.black};
      font-size: 9.875rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      strong {
        line-height: 0.86;
        font-size: 12.5rem;

        ${tp.useFont(fonts => fonts.Switzer.regular)};
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    grid-area: ${SECONDARY_CONTENT_AREAS.description};
    justify-content: center;
    z-index: 1;
    background-color: #5b5b5b66;

    & > p {
      padding: 7rem 4.5rem;
      text-align: center;
      line-height: 1.04;
      letter-spacing: -0.06em;
      color: ${colors.white};
      font-size: 3rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const IconsContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isRight',
})<{
  isRight?: boolean;
}>`
  ${({ isRight }) => css`
    display: flex;
    flex-direction: column;
    grid-area: ${isRight ? SECONDARY_CONTENT_AREAS.rightIcon : SECONDARY_CONTENT_AREAS.leftIcon};
    align-items: center;
    justify-content: space-around;
  `}
`;

const Icon = styled(GatsbyImage)`
  width: 43.5rem;
  height: 19.875rem;

  img {
    object-fit: contain !important;
  }
`;

const SecondaryContent: React.FC<ISecondaryContent> = ({
  title,
  description,
  leftIcons,
  rightIcons,
}) => {
  return (
    <Container>
      <IconsContainer>
        {leftIcons.map((iconProps, index) => (
          <Icon key={`${iconProps.alt}${index}`} {...iconProps} />
        ))}
      </IconsContainer>
      <Title render={title} />
      <Description render={description} />
      <IconsContainer isRight>
        {rightIcons.map((iconProps, index) => (
          <Icon key={`${iconProps.alt}${index}`} {...iconProps} />
        ))}
      </IconsContainer>
    </Container>
  );
};

export const desktopDualBlockEndorsementsSecondaryContentFragment = graphql`
  fragment SolutionDesktopDualBlockEndorsementsSecondaryContentData on PrismicSolutionDataDesktopSolutionDualBlockEndorsements {
    primary {
      solution_dual_block_endorsements_secondary_title {
        richText
      }
      solution_dual_block_endorsements_secondary_description {
        richText
      }
    }

    items {
      solution_dual_block_endorsements_icon {
        gatsbyImageData
        alt
      }
    }
  }
`;

export default SecondaryContent;
